/**
 * Frontend side of the `__STLMC_MESSAGES__` protocol.
 *
 * When the CRM runs inside the browser-extension modal iframe, it talks to the
 * extension content script (the parent window) over window.postMessage. This is
 * the requester half: it sends a typed request to `window.parent` and resolves a
 * promise when the matching response (same messageId, `_response: true`) arrives.
 *
 * The extension owns the auth token (in chrome.storage); we never touch cookies
 * or localStorage for it — every exchange goes through this protocol.
 */

const MESSAGE_SOURCE = '__STLMC_MESSAGES__'
const REQUEST_TIMEOUT_MS = 30000
// The token fetch gates auth on protected routes, so fail fast rather than
// leaving the user waiting if the extension is slow or unreachable.
const TOKEN_TIMEOUT_MS = 3000

const pending = new Map()
let listening = false

function generateMessageId() {
  return `stlmc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function ensureListener() {
  if (listening) return
  listening = true
  window.addEventListener('message', (event) => {
    const data = event.data
    if (!data || data.source !== MESSAGE_SOURCE || !data._response) return

    const entry = pending.get(data.messageId)
    if (!entry) return

    clearTimeout(entry.timeout)
    pending.delete(data.messageId)

    if (Array.isArray(data.errors) && data.errors.length) {
      entry.reject(new Error(data.errors.join('; ')))
    } else {
      entry.resolve(data.payload)
    }
  })
}

/**
 * Send a request to the extension and await its response payload.
 * @param {string} type one of the STLMC_* message types
 * @param {object} payload
 * @param {number} [timeoutMs] how long to wait for a response before rejecting
 * @returns {Promise<object>}
 */
export function sendMessage(type, payload = {}, timeoutMs = REQUEST_TIMEOUT_MS) {
  ensureListener()

  return new Promise((resolve, reject) => {
    const messageId = generateMessageId()

    const timeout = setTimeout(() => {
      pending.delete(messageId)
      reject(new Error(`Extension message ${type} timed out`))
    }, timeoutMs)

    pending.set(messageId, { resolve, reject, timeout })

    window.parent.postMessage(
      { source: MESSAGE_SOURCE, messageId, type, payload },
      '*'
    )
  })
}

export const getStoredToken = () => sendMessage('STLMC_GET_TOKEN', {}, TOKEN_TIMEOUT_MS)
export const storeToken = (token) => sendMessage('STLMC_SET_TOKEN', { token })
export const clearStoredToken = () => sendMessage('STLMC_CLEAR_TOKEN')

/**
 * Ask the extension to search the connected EMR for patients.
 * @param {string} query free-text search (name)
 * @returns {Promise<{ patients: Array<{ emrIntegrationId, firstName, lastName, dateOfBirth, mobilePhone?, homePhone?, workPhone?, emailAddress? }> }>}
 */
export const searchEmrPatients = (query) => sendMessage('STLMC_SEARCH_PATIENTS', { query })

/**
 * Ask the extension to fetch a patient's medications from the connected EMR.
 * @param {string} emrIntegrationId the patient's EMR integration id (ehrId)
 * @returns {Promise<{ medications: Array<{ emrMedicationId, drugName, sig, startDate, stopDate, isActive, isControlledSubstance }> }>}
 */
export const getEmrMedications = (emrIntegrationId) => sendMessage('STLMC_GET_MEDICATIONS', { emrIntegrationId })

/**
 * Ask the extension to fetch a patient's documents from the connected EMR.
 * @param {string} emrIntegrationId the patient's EMR integration id (ehrId)
 * @returns {Promise<{ documents: Array<{ emrDocumentId, name, documentType, documentDate, status, comments }> }>}
 */
export const getEmrDocuments = (emrIntegrationId) => sendMessage('STLMC_GET_DOCUMENTS', { emrIntegrationId })

/**
 * Ask the extension to fetch a patient's lab orders and results from the EMR.
 * @param {string} emrIntegrationId the patient's EMR integration id (ehrId)
 * @returns {Promise<{ orders: Array<{ emrOrderId, orderIdentifier, status, orderedDate, tests }>, results: Array<{ emrResultId, receivedDate, summary }> }>}
 */
export const getEmrLabs = (emrIntegrationId) => sendMessage('STLMC_GET_LABS', { emrIntegrationId })
